<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import OmniTextField from "@/components/omni/OmniTextField.vue";
import OmniButtonToggle from "@/components/omni/OmniButtonToggle.vue";
import { computed, ref, watch } from "vue";
import type { TruckDto, TruckType } from "@/api/trucks/dto/truck.dto";
import { TruckStatus } from "@/constants/truck";
import TruckTypeSelect from "@/components/selects/TruckTypeSelect.vue";
import TruckStatusSelect from "@/components/selects/TruckStatusSelect.vue";
import LocationPicker from "@/components/pickers/LocationPicker.vue";
import type { AddressDto } from "@/api/geo/dto/address.dto";
import { USA_MAP_CENTER } from "@/constants/map";
import type { GoogleMapCoordinates } from "@/api/dto/coordinates.dto";
import type { TruckLocationSource } from "@/types/trucks";

const mapCenter = defineModel<GoogleMapCoordinates>("mapCenter", { default: USA_MAP_CENTER });
const trucks = defineModel<TruckDto[]>("trucks", { default: () => [] });
const locationSource = defineModel<TruckLocationSource>("locationSource", { default: "current" });

const props = defineProps<{
  isTrucksLoaded: boolean;
}>();

const { t } = useI18n();

const location = ref<AddressDto | null>(null);
const truckNumber = ref<string>("");
const truckTypes = ref<TruckType[]>([]);
const truckStatuses = ref<TruckStatus[]>([]);
const isExclusive = ref<boolean>(false);
const isFiltersVisible = ref<boolean>(true);

const allTrucks = ref<TruckDto[]>([]);
let isInternalUpdate = false;

const filteredTrucks = computed<TruckDto[]>(() => {
  const numberQuery = truckNumber.value.trim().toLowerCase();

  return allTrucks.value.filter((truck) => {
    const matchesNumber = !numberQuery || truck.number.toLowerCase().includes(numberQuery);
    const matchesTypes = !truckTypes.value.length || truckTypes.value.includes(truck.type as TruckType);
    const matchesStatuses =
      !truckStatuses.value.length || (!!truck.status && truckStatuses.value.includes(truck.status));
    const matchesExclusive = !isExclusive.value || truck.exclusive;

    return matchesNumber && matchesTypes && matchesStatuses && matchesExclusive;
  });
});

const isCurrentLocation = computed(() => {
  return locationSource.value === "current";
});

const isAvailableLocation = computed(() => {
  return locationSource.value === "available";
});

function onLocationSearch(location: AddressDto | null): void {
  if (location) {
    mapCenter.value = {
      lat: location.lat,
      lng: location.lng,
    };
  }
}

function handleFilterVisibility() {
  if (props.isTrucksLoaded) {
    isFiltersVisible.value = !isFiltersVisible.value;
  }
}

watch(
  trucks,
  (value) => {
    if (isInternalUpdate) {
      isInternalUpdate = false;
      return;
    }

    allTrucks.value = value ?? [];
  },
  { immediate: true },
);

watch(filteredTrucks, (value) => {
  isInternalUpdate = true;
  trucks.value = value;
});

watch(
  () => props.isTrucksLoaded,
  (value) => {
    isFiltersVisible.value = value;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div>
    <div class="d-flex align-center col-gap-4">
      <location-picker
        v-model="location"
        :placeholder="t('trucks.search-city-or-zip')"
        :width="350"
        color="white"
        density="comfortable"
        label=""
        variant="solo"
        @update:model-value="onLocationSearch"
      />

      <v-btn flat height="44" @click="handleFilterVisibility()">
        <template #prepend>
          <v-icon size="24">mdi-filter-variant</v-icon>
        </template>

        <span class="font-weight-500">
          {{ t("general.filters") }}
        </span>
      </v-btn>
    </div>

    <transition name="fade">
      <div v-if="isFiltersVisible" class="bg-white pa-5 rounded-lg mt-4">
        <div class="d-flex align-center col-gap-2">
          <v-icon color="grey-darken-1" size="24">mdi-filter-variant</v-icon>

          <span> {{ t("general.filters") }}</span>
        </div>

        <div class="mt-4">{{ t("general.show-by") }}</div>

        <omni-button-toggle v-model="locationSource" class="mt-3" space-between>
          <v-btn
            :class="{
              'bg-indigo-lighten-5 border-primary': isCurrentLocation,
              'border-grey-lighter-1': !isCurrentLocation,
            }"
            :variant="isCurrentLocation ? 'outlined' : 'flat'"
            border="sm"
            class="rounded-lg"
            value="current"
            width="210"
          >
            <span
              :class="{
                'text-primary': isCurrentLocation,
              }"
              class="font-size-13 text-grey-darken-2 font-weight-500"
            >
              {{ t("trucks.current-location") }}</span
            >
          </v-btn>

          <v-btn
            :class="{
              'bg-indigo-lighten-5 border-primary': isAvailableLocation,
              'border-grey-lighter-1': !isAvailableLocation,
            }"
            :variant="isAvailableLocation ? 'outlined' : 'flat'"
            border="md"
            class="rounded-lg"
            value="available"
            width="210"
          >
            <span
              :class="{
                'text-primary': isAvailableLocation,
              }"
              class="font-size-13 text-grey-darken-2 font-weight-500"
            >
              {{ t("trucks.available-location") }}</span
            >
          </v-btn>
        </omni-button-toggle>

        <omni-text-field v-model="truckNumber" :label="t('trucks.truck-number')" class="mt-4"></omni-text-field>

        <div class="d-flex align-center justify-space-between w-100 col-gap-4 mt-4">
          <truck-type-select v-model="truckTypes" :label="t('trucks.truck-type')" multiple />

          <truck-status-select v-model="truckStatuses" :label="t('trucks.truck-status')" multiple />
        </div>

        <div class="d-flex align-center justify-space-between mt-4">
          <div class="d-flex align-center col-gap-3">
            <div
              class="d-flex justify-center align-center bg-amber-lighten-4"
              style="width: 24px; height: 24px; border-radius: 6px"
            >
              <v-icon class="rounded text-amber-darken-2" size="18">mdi-star</v-icon>
            </div>

            <span class="text-grey-darken-1">{{ t("trucks.exclusives-only") }}</span>
          </div>

          <v-switch v-model="isExclusive" color="primary" density="compact" hide-details />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
